/**
 * School Module Pages - Extracted from App.js
 * Contains: TeachersPage, AttendancePage, FeesPage, CommunicationPage, ComplaintsPage
 */
import React, { useState, useEffect } from 'react';
import { Modal, Toast } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Helper
const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) {
      return { error: err.message };
    }
  }
};

// ==================== TEACHERS PAGE ====================
function AddTeacherForm({ onSubmit }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ employee_id: '', name: '', gender: 'male', phone: '', email: '', qualification: '', designation: 'Teacher' });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4" data-testid="add-teacher-form">
      <input placeholder={L('Employee ID *', 'कर्मचारी ID *')} value={formData.employee_id} onChange={(e) => setFormData({...formData, employee_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <input placeholder={L('Name *', 'नाम *')} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <select value={formData.gender} onChange={(e) => setFormData({...formData, gender: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="male">{L('Male', 'पुरुष')}</option>
        <option value="female">{L('Female', 'महिला')}</option>
      </select>
      <input placeholder={L('Phone *', 'फ़ोन *')} value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <input placeholder={L('Email', 'ईमेल')} type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
      <input placeholder={L('Qualification', 'योग्यता')} value={formData.qualification} onChange={(e) => setFormData({...formData, qualification: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
      <select value={formData.designation} onChange={(e) => setFormData({...formData, designation: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="Teacher">{L('Teacher', 'शिक्षक')}</option>
        <option value="Senior Teacher">{L('Senior Teacher', 'वरिष्ठ शिक्षक')}</option>
        <option value="Head Teacher">{L('Head Teacher', 'प्रधान शिक्षक')}</option>
        <option value="Principal">{L('Principal', 'प्राचार्य')}</option>
      </select>
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Add Teacher', 'शिक्षक जोड़ें')}</button>
    </form>
  );
}

export function TeachersPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { loadTeachers(); }, []);

  const loadTeachers = async () => {
    const res = await api.get('/api/teachers/', token);
    setTeachers(res.teachers || []);
    setLoading(false);
  };

  const handleAddTeacher = async (data) => {
    await api.post('/api/teachers/', data, token);
    loadTeachers();
  };

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6" data-testid="teachers-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">{L('Teachers', 'शिक्षक')} ({teachers.length})</h2>
        <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600" data-testid="add-teacher-btn">+ {L('Add Teacher', 'शिक्षक जोड़ें')}</button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Emp. ID', 'कर्म. ID')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Name', 'नाम')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Designation', 'पदनाम')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Phone', 'फ़ोन')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{L('Status', 'स्थिति')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {teachers.map(teacher => (
              <tr key={teacher.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">{teacher.employee_id}</td>
                <td className="px-4 py-3">{teacher.name}</td>
                <td className="px-4 py-3">{teacher.designation}</td>
                <td className="px-4 py-3 text-gray-500">{teacher.phone}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${teacher.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {teacher.is_active ? L('Active', 'सक्रिय') : L('Inactive', 'निष्क्रिय')}
                  </span>
                </td>
              </tr>
            ))}
            {teachers.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">{L('No teachers found', 'कोई शिक्षक नहीं मिला')}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={L('Add New Teacher', 'नया शिक्षक जोड़ें')}>
        <AddTeacherForm onSubmit={(data) => { handleAddTeacher(data); setShowAddModal(false); }} />
      </Modal>
    </div>
  );
}

// ==================== ATTENDANCE PAGE ====================
export function AttendancePage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    const res = await api.get('/api/classes/', token);
    setClasses(res.classes || []);
    setLoading(false);
  };

  const loadStudents = async () => {
    if (!selectedClass || !selectedSection) return;
    const res = await api.get(`/api/students/?class_id=${selectedClass}&section_id=${selectedSection}`, token);
    setStudents(res.students || []);
    
    const attRes = await api.get(`/api/attendance/class/${selectedClass}/${selectedSection}?date=${selectedDate}`, token);
    const attMap = {};
    (attRes.attendance || []).forEach(a => { attMap[a.student_id] = a.status; });
    setAttendance(attMap);
  };

  useEffect(() => { loadStudents(); }, [selectedClass, selectedSection, selectedDate]);

  const handleAttendanceChange = (studentId, status) => {
    setAttendance(prev => ({ ...prev, [studentId]: status }));
  };

  const handleMarkAll = (status) => {
    const newAtt = {};
    students.forEach(s => { newAtt[s.id] = status; });
    setAttendance(newAtt);
  };

  const handleSave = async () => {
    setSaving(true);
    const records = students.map(s => ({ student_id: s.id, status: attendance[s.id] || 'present' }));
    const res = await api.post('/api/attendance/mark', {
      class_id: selectedClass,
      section_id: selectedSection,
      date: new Date(selectedDate).toISOString(),
      records
    }, token);
    setSaving(false);
    if (res.success) {
      setToast({ message: L('Attendance saved!', 'उपस्थिति सहेजी गई!'), type: 'success' });
    }
  };

  const sections = classes.find(c => c.id === selectedClass)?.sections || [];

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <h2 className="text-2xl font-bold text-gray-800">{L('Attendance', 'उपस्थिति')}</h2>

      <div className="bg-white rounded-xl shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select value={selectedClass} onChange={(e) => { setSelectedClass(e.target.value); setSelectedSection(''); }} className="px-4 py-2 border rounded-lg">
            <option value="">{L('Select Class', 'कक्षा चुनें')}</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selectedSection} onChange={(e) => setSelectedSection(e.target.value)} className="px-4 py-2 border rounded-lg" disabled={!selectedClass}>
            <option value="">{L('Select Section', 'सेक्शन चुनें')}</option>
            {sections.map(s => <option key={s.id} value={s.id}>{L('Section', 'सेक्शन')} {s.name}</option>)}
          </select>
          <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-4 py-2 border rounded-lg" />
          <div className="flex gap-2">
            <button onClick={() => handleMarkAll('present')} className="flex-1 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">{L('All Present', 'सब उपस्थित')}</button>
            <button onClick={() => handleMarkAll('absent')} className="flex-1 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200">{L('All Absent', 'सब अनुपस्थित')}</button>
          </div>
        </div>
      </div>

      {students.length > 0 && (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">{L('Roll No', 'रोल नं')}</th>
                <th className="px-4 py-3 text-left">{L('Name', 'नाम')}</th>
                <th className="px-4 py-3 text-center">{L('Present', 'उपस्थित')}</th>
                <th className="px-4 py-3 text-center">{L('Absent', 'अनुपस्थित')}</th>
                <th className="px-4 py-3 text-center">{L('Late', 'विलंब')}</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {students.map((student, idx) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3">{student.name}</td>
                  <td className="px-4 py-3 text-center">
                    <input type="radio" name={`att-${student.id}`} checked={attendance[student.id] === 'present' || !attendance[student.id]}
                      onChange={() => handleAttendanceChange(student.id, 'present')} className="w-5 h-5 accent-green-500" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input type="radio" name={`att-${student.id}`} checked={attendance[student.id] === 'absent'}
                      onChange={() => handleAttendanceChange(student.id, 'absent')} className="w-5 h-5 accent-red-500" />
                  </td>
                  <td className="px-4 py-3 text-center">
                    <input type="radio" name={`att-${student.id}`} checked={attendance[student.id] === 'late'}
                      onChange={() => handleAttendanceChange(student.id, 'late')} className="w-5 h-5 accent-yellow-500" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 border-t">
            <button onClick={handleSave} disabled={saving} className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50" data-testid="save-attendance-btn">
              {saving ? L('Saving...', 'सहेज रहे हैं...') : L('Save Attendance', 'उपस्थिति सहेजें')}
            </button>
          </div>
        </div>
      )}

      {selectedClass && selectedSection && students.length === 0 && (
        <div className="text-center py-12 text-gray-500">{L('No students in this section', 'इस सेक्शन में कोई विद्यार्थी नहीं')}</div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

// ==================== FEES PAGE ====================
function AddFeePlanForm({ classes, token, onSuccess }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ name: '', class_id: '', academic_year: '2025-26', fee_heads: [{ name: 'Tuition Fee', amount: 0 }] });

  const addFeeHead = () => {
    setFormData({ ...formData, fee_heads: [...formData.fee_heads, { name: '', amount: 0 }] });
  };

  const updateFeeHead = (idx, field, value) => {
    const heads = [...formData.fee_heads];
    heads[idx][field] = field === 'amount' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, fee_heads: heads });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post(`/api/fees/plans?name=${formData.name}&class_id=${formData.class_id}&academic_year=${formData.academic_year}&fee_heads=${JSON.stringify(formData.fee_heads)}`, {}, token);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input placeholder={L('Plan Name', 'योजना का नाम')} value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <select value={formData.class_id} onChange={(e) => setFormData({...formData, class_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required>
        <option value="">{L('Select Class', 'कक्षा चुनें')}</option>
        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      <div className="space-y-2">
        <label className="text-sm font-medium">{L('Fee Heads', 'शुल्क मद')}</label>
        {formData.fee_heads.map((head, idx) => (
          <div key={idx} className="flex gap-2">
            <input placeholder={L('Fee Name', 'शुल्क नाम')} value={head.name} onChange={(e) => updateFeeHead(idx, 'name', e.target.value)} className="flex-1 px-3 py-2 border rounded-lg" />
            <input type="number" placeholder={L('Amount', 'राशि')} value={head.amount} onChange={(e) => updateFeeHead(idx, 'amount', e.target.value)} className="w-32 px-3 py-2 border rounded-lg" />
          </div>
        ))}
        <button type="button" onClick={addFeeHead} className="text-orange-600 text-sm">+ {L('Add Fee Head', 'शुल्क मद जोड़ें')}</button>
      </div>
      <div className="pt-2 border-t">
        <p className="text-right font-semibold">{L('Total', 'कुल')}: ₹{formData.fee_heads.reduce((sum, h) => sum + h.amount, 0)}</p>
      </div>
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Create Plan', 'योजना बनाएं')}</button>
    </form>
  );
}

function CollectFeeForm({ feePlans, token, onSuccess }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ student_id: '', fee_plan_id: '', amount_paid: '', payment_method: 'cash', discount: 0 });
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const searchStudents = async (query) => {
    if (query.length < 2) return;
    const res = await api.get(`/api/students/?search=${query}`, token);
    setStudents(res.students || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await api.post('/api/fees/payments', {
      student_id: formData.student_id,
      fee_plan_id: formData.fee_plan_id,
      amount_paid: parseFloat(formData.amount_paid),
      payment_method: formData.payment_method,
      discount: parseFloat(formData.discount) || 0
    }, token);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <input placeholder={L('Search student by name...', 'नाम से विद्यार्थी खोजें...')} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); searchStudents(e.target.value); }}
          className="w-full px-4 py-2 border rounded-lg" />
        {students.length > 0 && (
          <div className="mt-2 border rounded-lg max-h-40 overflow-y-auto">
            {students.map(s => (
              <button key={s.id} type="button" onClick={() => { setFormData({...formData, student_id: s.id}); setSearchQuery(s.name); setStudents([]); }}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0">
                {s.name} ({s.admission_number})
              </button>
            ))}
          </div>
        )}
      </div>
      <select value={formData.fee_plan_id} onChange={(e) => setFormData({...formData, fee_plan_id: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required>
        <option value="">{L('Select Fee Plan', 'शुल्क योजना चुनें')}</option>
        {feePlans.map(p => <option key={p.id} value={p.id}>{p.class_name} - ₹{p.total_amount}</option>)}
      </select>
      <input type="number" placeholder={L('Amount Paid *', 'भुगतान राशि *')} value={formData.amount_paid} onChange={(e) => setFormData({...formData, amount_paid: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <input type="number" placeholder={L('Discount', 'छूट')} value={formData.discount} onChange={(e) => setFormData({...formData, discount: e.target.value})} className="w-full px-4 py-2 border rounded-lg" />
      <select value={formData.payment_method} onChange={(e) => setFormData({...formData, payment_method: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="cash">{L('Cash', 'नकद')}</option>
        <option value="online">{L('Online', 'ऑनलाइन')}</option>
        <option value="cheque">{L('Cheque', 'चेक')}</option>
        <option value="upi">UPI</option>
      </select>
      <button type="submit" className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600">{L('Record Payment', 'भुगतान दर्ज करें')}</button>
    </form>
  );
}

export function FeesPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [classes, setClasses] = useState([]);
  const [feePlans, setFeePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlan, setShowAddPlan] = useState(false);
  const [showCollect, setShowCollect] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [classesRes, plansRes] = await Promise.all([
        api.get('/api/classes/', token),
        api.get('/api/fees/plans', token)
      ]);
      setClasses(classesRes.classes || []);
      setFeePlans(plansRes.fee_plans || []);
    } catch (err) {
      console.error('Error loading fees data:', err);
      setClasses([]);
      setFeePlans([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6" data-testid="fees-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">{L('Fee Management', 'शुल्क प्रबंधन')}</h2>
        <div className="flex gap-2">
          <button onClick={() => setShowAddPlan(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600" data-testid="create-fee-plan-btn">+ {L('Create Fee Plan', 'शुल्क योजना बनाएं')}</button>
          <button onClick={() => setShowCollect(true)} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600" data-testid="collect-fee-btn">💰 {L('Collect Fee', 'शुल्क लें')}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {feePlans.map(plan => (
          <div key={plan.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="font-semibold">{plan.class_name || plan.name}</h3>
                <p className="text-sm text-gray-500">{plan.academic_year}</p>
              </div>
              <span className="text-xl font-bold text-orange-600">₹{plan.total_amount}</span>
            </div>
            <div className="space-y-1">
              {plan.fee_heads?.map((head, idx) => (
                <div key={idx} className="flex justify-between text-sm">
                  <span className="text-gray-600">{head.name}</span>
                  <span>₹{head.amount}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {feePlans.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <p className="text-4xl mb-4">💰</p>
          <p>{L('No fee plans created yet', 'अभी तक कोई शुल्क योजना नहीं बनी')}</p>
        </div>
      )}

      <Modal isOpen={showAddPlan} onClose={() => setShowAddPlan(false)} title={L('Create Fee Plan', 'शुल्क योजना बनाएं')}>
        <AddFeePlanForm classes={classes} token={token} onSuccess={() => { loadData(); setShowAddPlan(false); }} />
      </Modal>

      <Modal isOpen={showCollect} onClose={() => setShowCollect(false)} title={L('Collect Fee', 'शुल्क लें')}>
        <CollectFeeForm feePlans={feePlans} token={token} onSuccess={() => { loadData(); setShowCollect(false); }} />
      </Modal>
    </div>
  );
}

// ==================== COMMUNICATION PAGE ====================
function AddAnnouncementForm({ onSubmit }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ title: '', message: '', target_audience: 'all', priority: 'normal' });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <input placeholder={L('Title *', 'शीर्षक *')} value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <textarea placeholder={L('Message *', 'संदेश *')} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-32" required />
      <select value={formData.target_audience} onChange={(e) => setFormData({...formData, target_audience: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="all">{L('All', 'सभी')}</option>
        <option value="teachers">{L('Teachers Only', 'केवल शिक्षक')}</option>
        <option value="parents">{L('Parents Only', 'केवल अभिभावक')}</option>
        <option value="students">{L('Students Only', 'केवल विद्यार्थी')}</option>
      </select>
      <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="normal">{L('Normal', 'सामान्य')}</option>
        <option value="important">{L('Important', 'महत्वपूर्ण')}</option>
        <option value="urgent">{L('Urgent', 'अत्यावश्यक')}</option>
      </select>
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Send Announcement', 'घोषणा भेजें')}</button>
    </form>
  );
}

export function CommunicationPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    const res = await api.get('/api/communication/announcements', token);
    setAnnouncements(res.announcements || []);
    setLoading(false);
  };

  const handleAdd = async (data) => {
    await api.post('/api/communication/announcements', data, token);
    loadAnnouncements();
  };

  const priorityLabel = (p) => ({
    urgent: L('urgent', 'अत्यावश्यक'),
    important: L('important', 'महत्वपूर्ण'),
    normal: L('normal', 'सामान्य'),
  }[p] || p);

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6" data-testid="communication-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">{L('Communication', 'संचार')}</h2>
        <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600" data-testid="new-announcement-btn">+ {L('New Announcement', 'नई घोषणा')}</button>
      </div>

      <div className="space-y-4">
        {announcements.map(ann => (
          <div key={ann.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-lg">{ann.title}</h3>
                <p className="text-gray-600 mt-1">{ann.message}</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs ${ann.priority === 'urgent' ? 'bg-red-100 text-red-700' : ann.priority === 'important' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}`}>
                {priorityLabel(ann.priority)}
              </span>
            </div>
            <div className="flex justify-between items-center mt-4 pt-3 border-t text-sm text-gray-500">
              <span>📢 {ann.target_audience}</span>
              <span>{new Date(ann.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
        {announcements.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-4">📢</p>
            <p>{L('No announcements yet', 'अभी तक कोई घोषणा नहीं')}</p>
          </div>
        )}
      </div>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={L('New Announcement', 'नई घोषणा')}>
        <AddAnnouncementForm onSubmit={(data) => { handleAdd(data); setShowAddModal(false); }} />
      </Modal>
    </div>
  );
}

// ==================== COMPLAINTS PAGE ====================
function AddComplaintForm({ onSubmit, token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [formData, setFormData] = useState({ subject: '', description: '', category: 'general', priority: 'medium', student_id: '' });
  const [students, setStudents] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const searchStudents = async (query) => {
    if (query.length < 2) return;
    const res = await api.get(`/api/students/?search=${query}`, token);
    setStudents(res.students || []);
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(formData); }} className="space-y-4">
      <div>
        <input placeholder={L('Search related student (optional)...', 'संबंधित विद्यार्थी खोजें (वैकल्पिक)...')} value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); searchStudents(e.target.value); }} className="w-full px-4 py-2 border rounded-lg" />
        {students.length > 0 && (
          <div className="mt-2 border rounded-lg max-h-32 overflow-y-auto">
            {students.map(s => (
              <button key={s.id} type="button" onClick={() => { setFormData({...formData, student_id: s.id}); setSearchQuery(s.name); setStudents([]); }}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 text-sm">{s.name}</button>
            ))}
          </div>
        )}
      </div>
      <select value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="general">{L('General', 'सामान्य')}</option>
        <option value="academic">{L('Academic', 'शैक्षणिक')}</option>
        <option value="discipline">{L('Discipline', 'अनुशासन')}</option>
        <option value="fee">{L('Fee Related', 'शुल्क संबंधी')}</option>
        <option value="transport">{L('Transport', 'परिवहन')}</option>
        <option value="facility">{L('Facility', 'सुविधा')}</option>
      </select>
      <input placeholder={L('Subject *', 'विषय *')} value={formData.subject} onChange={(e) => setFormData({...formData, subject: e.target.value})} className="w-full px-4 py-2 border rounded-lg" required />
      <textarea placeholder={L('Description *', 'विवरण *')} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-4 py-2 border rounded-lg h-24" required />
      <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-4 py-2 border rounded-lg">
        <option value="low">{L('Low Priority', 'कम प्राथमिकता')}</option>
        <option value="medium">{L('Medium Priority', 'मध्यम प्राथमिकता')}</option>
        <option value="high">{L('High Priority', 'उच्च प्राथमिकता')}</option>
      </select>
      <button type="submit" className="w-full py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600">{L('Submit Complaint', 'शिकायत दर्ज करें')}</button>
    </form>
  );
}

export function ComplaintsPage({ token }) {
  const { language } = useLanguage();
  const L = (en, hi) => (language === 'hi' ? hi : en);
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadComplaints(); }, []);

  const loadComplaints = async () => {
    const res = await api.get('/api/complaints/', token);
    setComplaints(res.complaints || []);
    setLoading(false);
  };

  const handleAdd = async (data) => {
    await api.post('/api/complaints/', data, token);
    loadComplaints();
  };

  const updateStatus = async (id, status) => {
    await api.post(`/api/complaints/${id}/status`, { status }, token);
    loadComplaints();
  };

  const filtered = filter === 'all' ? complaints : complaints.filter(c => c.status === filter);

  const filterLabel = (f) => ({
    all: L('All', 'सभी'),
    open: L('Open', 'खुला'),
    in_progress: L('In Progress', 'प्रगति में'),
    resolved: L('Resolved', 'सुलझाया'),
  }[f] || f);

  if (loading) return <div className="p-8 text-center">{L('Loading...', 'लोड हो रहा है...')}</div>;

  return (
    <div className="space-y-6" data-testid="complaints-page">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">{L('Complaints', 'शिकायतें')} ({complaints.length})</h2>
        <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600" data-testid="log-complaint-btn">+ {L('Log Complaint', 'शिकायत दर्ज करें')}</button>
      </div>

      <div className="flex gap-2">
        {['all', 'open', 'in_progress', 'resolved'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-lg ${filter === f ? 'bg-orange-500 text-white' : 'bg-gray-100'}`}>
            {filterLabel(f)}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filtered.map(complaint => (
          <div key={complaint.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-1 rounded text-xs ${complaint.category === 'academic' ? 'bg-blue-100 text-blue-700' : complaint.category === 'discipline' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                    {complaint.category}
                  </span>
                  <span className={`px-2 py-1 rounded text-xs ${complaint.priority === 'high' ? 'bg-red-100 text-red-700' : complaint.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                    {complaint.priority} {L('priority', 'प्राथमिकता')}
                  </span>
                </div>
                <h3 className="font-semibold">{complaint.subject}</h3>
                <p className="text-gray-600 text-sm mt-1">{complaint.description}</p>
              </div>
              <select value={complaint.status} onChange={(e) => updateStatus(complaint.id, e.target.value)}
                className={`px-3 py-1 rounded-lg text-sm border ${complaint.status === 'resolved' ? 'bg-green-50 text-green-700' : complaint.status === 'in_progress' ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700'}`}>
                <option value="open">{L('Open', 'खुला')}</option>
                <option value="in_progress">{L('In Progress', 'प्रगति में')}</option>
                <option value="resolved">{L('Resolved', 'सुलझाया')}</option>
              </select>
            </div>
            {complaint.student_name && (
              <p className="mt-2 text-sm text-gray-500">{L('Student', 'विद्यार्थी')}: {complaint.student_name}</p>
            )}
            <p className="mt-2 text-xs text-gray-400">{new Date(complaint.created_at).toLocaleString()}</p>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-4">📨</p>
            <p>{L('No complaints found', 'कोई शिकायत नहीं मिली')}</p>
          </div>
        )}
      </div>

      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title={L('Log New Complaint', 'नई शिकायत दर्ज करें')}>
        <AddComplaintForm onSubmit={(data) => { handleAdd(data); setShowAddModal(false); }} token={token} />
      </Modal>
    </div>
  );
}

export default {
  TeachersPage,
  AttendancePage,
  FeesPage,
  CommunicationPage,
  ComplaintsPage
};
